import { Section, Steps, Bullets, FeatureGrid, RelatedLinks, FinalCTA } from "@/components/v2/blocks";
import Seo from "@/components/v2/Seo";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import ProcessFlow from "@/components/v2/ProcessFlow";
import { CardContent } from "@/components/ui/card";
import TraceCard from "@/components/v2/TraceCard";
import { Check, ArrowRight, Settings2, Handshake, Building2, CalendarClock, FileText, LineChart, Gauge, ClipboardList, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const PROCESS = [
  { title: "Prise de contact" },
  { title: "Analyse du dossier" },
  { title: "Signature éventuelle de l'ACD" },
  { title: "Consultation fournisseurs" },
  { title: "Présentation des résultats" },
  { title: "Accompagnement" },
];

const DOMAINS = [
  { title: "Optimisation des contrats", text: "Analyse de la situation actuelle de l'entreprise." },
  { title: "Anticipation des contrats", text: "Préparation et anticipation des échéances." },
  { title: "Gestion multi-sites", text: "Accompagnement des structures disposant de plusieurs sites." },
  { title: "Syndics de copropriété", text: "Gestion et centralisation de portefeuilles énergétiques." },
  { title: "Analyse des factures", text: "Première lecture de la situation énergétique." },
];

export default function Professionnels() {
  return (
    <div>
      <Seo title="Énergie pour les professionnels" description="Optimisation, renégociation et gestion multi-sites de vos contrats d'énergie. Energy Market accompagne TPE, PME, groupes et copropriétés." path="/professionnels" />
      <PageHeroPhoto
        cmsKey="pro"
        eyebrow="Univers Professionnels"
        title="Votre entreprise mérite un accompagnement énergétique à la hauteur de ses enjeux"
        subtitle="Entre les échéances contractuelles, les évolutions du marché et la diversité des fournisseurs, il n'est pas toujours simple de savoir quelle stratégie adopter. Energy Market accompagne les professionnels dans l'analyse de leurs contrats d'électricité et de gaz pour leur apporter une vision claire et structurée."
        image="/img/hero-b2b.png"
        overlay="brand-light"
        highlights={[
          "Optimisation & renégociation des contrats",
          "Anticipation des échéances contractuelles",
          "Gestion multi-sites & copropriétés",
          "Analyse détaillée de vos factures",
        ]}
      />

      {/* NOS SERVICES — contenu des 3 pages Pro */}
      <Section cmsKey="pro.domaines" eyebrow="Nos domaines d'intervention" title="Trois accompagnements sur mesure pour les professionnels">
        <div className="grid lg:grid-cols-3 gap-6">

          {/* Optimisation des contrats */}
          <TraceCard index={0} radius={24} accent="#2E7D62" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: "#2E7D62" }}>
                <Settings2 className="h-6 w-6" strokeWidth={2.1} aria-hidden />
              </div>
              <p className="em-eyebrow mb-2">Optimisation</p>
              <h3 className="font-display text-xl font-bold text-primary leading-snug">
                Optimisation des contrats d'énergie
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Les besoins énergétiques d'une entreprise évoluent constamment. Un contrat adapté il y a quelques années ne l'est pas nécessairement aujourd'hui.
              </p>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Votre entreprise change, votre contrat aussi :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Activité", "Effectifs", "Équipements", "Horaires", "Organisation interne", "Consommation"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Au-delà du prix :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Les échéances", "Les caractéristiques du contrat", "Les données techniques", "Les évolutions de l'entreprise"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/professionnels/optimisation-des-contrats" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </TraceCard>

          {/* Rénégociation des contrats */}
          <TraceCard index={1} radius={24} accent="#7AB317" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: "#7AB317" }}>
                <Handshake className="h-6 w-6" strokeWidth={2.1} aria-hidden />
              </div>
              <p className="em-eyebrow mb-2">Rénégociation</p>
              <h3 className="font-display text-xl font-bold text-primary leading-snug">
                Anticipez la rénégociation de vos contrats
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Les échéances contractuelles sont des moments clés. Une préparation anticipée permet de les aborder dans de meilleures conditions.
              </p>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Le temps est votre meilleur allié :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Une meilleure visibilité", "Davantage de temps pour analyser les solutions", "Une consultation fournisseurs plus structurée", "Une prise de décision plus sereine"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Notre accompagnement :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Analyse du contrat actuel", "Identification de l'échéance", "Signature éventuelle de l'ACD", "Consultation fournisseurs", "Présentation des résultats", "Accompagnement"].map((x, i) => (
                  <li key={x} className="flex gap-2">
                    <span className="font-bold text-accent text-xs w-4 shrink-0 mt-0.5">{String(i+1).padStart(2,'0')}</span>{x}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/professionnels/anticipation-des-contrats" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </TraceCard>

          {/* Gestion multi-sites */}
          <TraceCard index={2} radius={24} accent="#0E9C8A" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: "#0E9C8A" }}>
                <Building2 className="h-6 w-6" strokeWidth={2.1} aria-hidden />
              </div>
              <p className="em-eyebrow mb-2">Multi-sites</p>
              <h3 className="font-display text-xl font-bold text-primary leading-snug">
                Centralisez la vision de vos contrats
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Lorsque plusieurs établissements ou immeubles sont concernés, la gestion énergétique devient plus complexe. Energy Market accompagne les organisations multi-sites.
              </p>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Pour qui ?</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Entreprises multi-sites", "Réseaux commerciaux", "Franchises", "Groupes d'entreprises", "Sites industriels", "Syndics de copropriété"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Les avantages :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Vision globale du portefeuille", "Anticipation des échéances", "Simplification administrative", "Consultations facilitées"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/professionnels/gestion-multi-sites" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </TraceCard>

        </div>
      </Section>

      {/* POURQUOI SE FAIRE ACCOMPAGNER — panneau premium */}
      <section className="bg-primary text-primary-foreground">
        <div className="em-container em-section">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Pourquoi se faire accompagner ?</p>
                <h2 className="mt-3 font-display text-3xl md:text-4xl font-black leading-tight">L'énergie est devenue un poste stratégique</h2>
                <p className="mt-5 leading-relaxed text-primary-foreground/80">
                  Les dirigeants disposent rarement du temps nécessaire pour suivre en permanence les évolutions du marché.
                  Les contrats peuvent comporter de nombreuses spécificités qu'il faut maîtriser.
                </p>
              </div>
              <div className="lg:border-l lg:border-white/15 lg:pl-14">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">Ce qu'il faut suivre en permanence</p>
                <ul className="mt-6 space-y-4">
                  {[
                    { label: "Échéances contractuelles", icon: CalendarClock },
                    { label: "Modalités contractuelles", icon: FileText },
                    { label: "Mécanismes tarifaires", icon: LineChart },
                    { label: "Caractéristiques techniques", icon: Gauge },
                    { label: "Conditions particulières", icon: ClipboardList },
                  ].map(({ label, icon: Icon }) => (
                    <li key={label} className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-accent">
                        <Icon className="h-5 w-5" aria-hidden />
                      </span>
                      <span className="font-medium">{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

      <Section cmsKey="pro.how" eyebrow="Comment ça fonctionne ?" title="Un accompagnement structuré, de bout en bout" muted>
        <ProcessFlow steps={PROCESS} />
      </Section>

      <Section cmsKey="pro.acd" eyebrow="Qu'est-ce qu'une ACD ?" title="L'Autorisation de Collecte de Données" intro="L'ACD est un document permettant d'obtenir certaines informations nécessaires à la consultation de plusieurs fournisseurs. Elle intervient après la réception du formulaire de contact et avant les consultations fournisseurs.">
        <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
          <div className="flex items-center gap-3 border-b border-border pb-5">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ShieldCheck className="h-6 w-6" aria-hidden />
            </span>
            <p className="font-display text-lg font-bold text-primary">Un document simple, transparent et sans risque</p>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {[
              "Elle est gratuite.",
              "Elle est sans engagement.",
              "Elle n'entraîne aucun changement de fournisseur.",
              "Elle permet simplement d'accéder aux données nécessaires à l'étude du dossier.",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
                </span>
                <span className="leading-relaxed text-foreground">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>



      <FinalCTA title="Faites le point sur vos contrats d'énergie" />
    </div>
  );
}
